import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit, effect, inject, input, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { MSS_FORMLY_IMPORTS } from '@shared/components/formly/formly-standalone-imports';
import { FormFieldType, WrapperType } from '@shared/components/formly/formly.config';
import { FormsValidators } from '@shared/components/forms/validators';
import { FindAddressResponse } from '@shared/models';
import { createComplaintsFormFields } from '@shared/models/complaints-preferences';
import { AddressSearchType, DocumentPreferences, EvidenceOfResearch } from '@shared/models/document-preferences.model';
import { DocumentPreferenceInfoType } from '@shared/models/enums/document-preferences.enum';
import { AddressLookupService, ToastService } from '@shared/services';
import { UserDetailsService } from '@shared/services/user-details.service';
import { firstValueFrom, tap } from 'rxjs';

@Component({
  selector: 'lib-document-preferences',
  templateUrl: './document-preferences.component.html',
  imports: [FormsModule, ReactiveFormsModule, ...MSS_FORMLY_IMPORTS],
})
export class DocumentPreferencesComponent implements OnInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly userDetailsService = inject(UserDetailsService);
  private readonly addressLookupService = inject(AddressLookupService);
  private readonly toastService = inject(ToastService);

  public readonly config = input<FormlyFieldConfig[]>();
  public localConfig: FormlyFieldConfig[] = [];

  public readonly form = input<UntypedFormGroup>();
  public localForm: UntypedFormGroup | undefined;

  public readonly model = input<DocumentPreferences>();
  public localModel: DocumentPreferences | undefined;

  public constructor() {
    effect(() => {
      const val = this.config();
      if (val) {
        this.localConfig = val;
      }
    });
    effect(() => {
      const val = this.form();
      if (val) {
        this.localForm = val;
      }
    });
    effect(() => {
      const val = this.model();
      if (val) {
        this.localModel = val;
      }
    });
  }

  public readonly showSave = input(true);
  public readonly requestForbidden = output();
  public readonly savePreferences = output<DocumentPreferences>();

  private defaultProps: Record<string, unknown> = {};
  public isManualAddress: AddressSearchType = { complaints: false, broker: false };
  private addressSearchSuccessful: AddressSearchType = { complaints: false, broker: false };
  public addresses: Record<'complaints' | 'broker', FindAddressResponse[]> = { complaints: [], broker: [] };

  public ngOnInit() {
    this.requestEsisDocPreferences();
  }

  public get isValid(): boolean {
    return this.localForm?.valid ?? false;
  }

  public get isManagedByOrganisation(): boolean {
    return !!this.localModel?.distributionGroupComplaints;
  }

  private requestEsisDocPreferences(): void {
    this.userDetailsService.getEsisDocPreferences().subscribe({
      next: (preferences: DocumentPreferences) => this.initializeForm(preferences),
      error: (error: HttpErrorResponse) => {
        if ((error.status as HttpStatusCode) === HttpStatusCode.Forbidden) {
          this.requestForbidden.emit(undefined);
        } else {
          this.initializeForm();
        }
      },
    });
  }

  private initializeForm(preferences?: DocumentPreferences): void {
    this.defaultProps = {};

    let m = {
      complaints: null,
      distributionGroupComplaints: null,
      evidenceOfResearch: null,
    } as DocumentPreferences;

    if (preferences) {
      m = preferences;
      if (!m.evidenceOfResearch?.numberOfResults) {
        m.evidenceOfResearch = {
          numberOfResults: '10',
          ...m.evidenceOfResearch,
        } as EvidenceOfResearch;
      }
      // should show manual address fields if pre-population
      this.isManualAddress.complaints = !!preferences.complaints;
      this.isManualAddress.broker = !!preferences.broker;
    }

    this.localModel = m;

    this.localConfig = this.localConfig.length > 0 ? this.localConfig : this.getConfig();

    if (!this.localForm) {
      this.localForm = new UntypedFormGroup({});
      this.localForm.patchValue(this.localModel);
    }
  }

  private getConfig(): FormlyFieldConfig[] {
    const { fields: complaintsFields } = createComplaintsFormFields(this.addressLookupService, () => this.localModel?.complaints ?? null, {
      isManualAddress: this.isManualAddress.complaints,
      onIsManualAddressChange: isManual => (this.isManualAddress.complaints = isManual),
      onAddressSearchSuccessfulChange: successful => (this.addressSearchSuccessful.complaints = successful),
      onAddressesChange: addresses => (this.addresses.complaints = addresses),
    });

    const { fields: brokerComplaintsFields } = createComplaintsFormFields(
      this.addressLookupService,
      () => this.localModel?.distributionGroupComplaints ?? null,
      { isManualAddress: true },
    );
    brokerComplaintsFields.forEach(f => ((f.props ?? {}).disabled = true));

    return [
      {
        type: FormFieldType.Template,
        className: 'h2',
        props: {
          label: 'Broker details',
        },
      },
      {
        key: 'broker',
        wrappers: [WrapperType.NoWrappers],
        fieldGroup: [
          {
            key: 'brokerName',
            type: FormFieldType.Input,
            props: {
              label: 'Broker name',
              ...this.defaultProps,
              required: true,
            },
          },
          {
            key: 'postcode',
            type: FormFieldType.PostCode,
            props: {
              label: 'Postcode',
              submitText: 'Find address',
              linkText: 'Enter address manually',
              ...this.defaultProps,
              required: true,
              setAddressType: this.setAddressSearchTypeBroker.bind(this),
              clickHandler: this.searchAddress.bind(this, DocumentPreferenceInfoType.BrokerDetails),
            },
            expressions: {
              hide: () => this.isManualAddress.broker,
            },
            validators: {
              validation: [FormsValidators.postcode],
            },
          },
          {
            key: 'addressLineLookUp',
            type: FormFieldType.Select,
            props: {
              label: 'Address',
              placeholder: 'Please Select...',
              options: [],
              ...this.defaultProps,
              required: true,
              change: field => this.prePopAddress(field.model[field.key as string], DocumentPreferenceInfoType.BrokerDetails),
            },
            expressions: {
              hide: () => !this.addressSearchSuccessful.broker || this.isManualAddress.broker,
            },
          },
          {
            key: 'addressLine1',
            type: FormFieldType.Input,
            props: {
              label: 'Address line 1',
              ...this.defaultProps,
              required: true,
            },
            expressions: {
              hide: () => !this.isManualAddress.broker,
            },
          },
          {
            key: 'addressLine2',
            type: FormFieldType.Input,
            props: {
              label: 'Address line 2',
              ...this.defaultProps,
            },
            expressions: {
              hide: () => !this.isManualAddress.broker,
            },
          },
          {
            key: 'town',
            type: FormFieldType.Input,
            props: {
              label: 'Town',
              ...this.defaultProps,
              required: true,
            },
            expressions: {
              hide: () => !this.isManualAddress.broker,
            },
          },
          {
            key: 'county',
            type: FormFieldType.Input,
            props: {
              label: 'County',
              ...this.defaultProps,
              required: false,
            },
            expressions: {
              hide: () => !this.isManualAddress.broker,
            },
          },
          {
            key: 'postcode',
            type: FormFieldType.PostCode,
            props: {
              label: 'Postcode',
              linkText: 'Search for address',
              setAddressType: this.setAddressSearchTypeBroker.bind(this),
              ...this.defaultProps,
              required: true,
            },
            expressions: {
              hide: () => !this.isManualAddress.broker,
            },
          },
          {
            key: 'telephone',
            type: FormFieldType.Input,
            props: {
              label: 'Telephone number',
              ...this.defaultProps,
              maxLength: 15,
              required: true,
            },
            validators: {
              telephone: {
                expression: (fc: UntypedFormControl) => !FormsValidators.telephone(fc),
                message: 'Enter valid telephone number',
              },
            },
          },
          {
            key: 'emailAddress',
            type: FormFieldType.Input,
            props: {
              label: 'Email address',
              inputType: 'email',
              ...this.defaultProps,
              required: false,
            },
            validators: {
              validation: [FormsValidators.email],
            },
          },
        ],
      },
      {
        type: FormFieldType.Template,
        className: 'h2',
        props: {
          label: 'Complaints information',
        },
      },
      // If the user is in a distribution group which locks the complaints info, show the fields as read-only with the broker
      // complaints info fill. If they aren't, then show an editable view of their own complaints info.
      this.localModel?.distributionGroupComplaints
        ? {
          key: 'distributionGroupComplaints',
          wrappers: [WrapperType.NoWrappers],
          fieldGroup: brokerComplaintsFields,
        }
        : {
          key: 'complaints',
          wrappers: [WrapperType.NoWrappers],
          fieldGroup: complaintsFields,
        },
      {
        template: '',
      },
      {
        type: FormFieldType.Template,
        className: 'h2',
        props: {
          label: 'Evidence of research',
        },
      },
      {
        key: 'evidenceOfResearch',
        wrappers: [WrapperType.NoWrappers],
        fieldGroup: [
          {
            key: 'numberOfResults',
            type: 'input',
            props: {
              label: 'Number of results to display',
              type: 'number',
              ...this.defaultProps,
              required: true,
            },
            validators: {
              validation: [FormsValidators.integer],
            },
          },
          {
            key: 'resultsGridTitle',
            type: 'input',
            props: {
              label: 'Results grid title',
              ...this.defaultProps,
              required: false,
            },
          },
          {
            key: 'resultsGridDescription',
            type: 'input',
            props: {
              label: 'Results grid description',
              ...this.defaultProps,
              required: false,
            },
          },
          {
            key: 'reasonForRecommendation',
            type: 'textarea',
            props: {
              label: 'Reason for recommendation',
              ...this.defaultProps,
              required: false,
            },
          },
          {
            key: 'additionalInformation',
            type: 'textarea',
            props: {
              label: 'Additional information',
              ...this.defaultProps,
              required: false,
            },
          },
        ],
      },
    ];
  }

  private setAddressSearchTypeBroker(): void {
    this.isManualAddress.broker = !this.isManualAddress.broker;
  }

  private prePopAddress(addressId: string, formType: DocumentPreferenceInfoType): void {
    const currentForm = this.selectFormByType(formType);
    if (currentForm === 'evidenceOfResearch' || currentForm === '') {
      return;
    }

    this.addressLookupService.retrieveAddress(addressId).subscribe(newAddress => {
      const [firstLine, ...secondLine] = newAddress.lines;

      if (!this.localModel) {
        return;
      }

      const selectedAddress = this.localModel[currentForm];
      if (!selectedAddress) {
        return;
      }

      // update model
      selectedAddress.addressLine1 = firstLine.trim();
      selectedAddress.addressLine2 = secondLine.join(', ').trim();
      selectedAddress.town = newAddress.town;
      selectedAddress.county = newAddress.county;
      selectedAddress.postcode = newAddress.postcode;
      // patch value in form
      this.localForm?.patchValue(this.localModel);
    });
  }

  public searchAddress(formType: DocumentPreferenceInfoType): void {
    const currentForm = this.selectFormByType(formType);
    if (currentForm === 'evidenceOfResearch' || currentForm === '') {
      return;
    }

    if (!FormsValidators.postcodeRegex.test(this.localForm?.value[currentForm].postcode)) {
      return;
    }
    this.addressLookupService.findAddress(this.localForm?.value[currentForm].postcode).subscribe(addresses => {
      this.addressSearchSuccessful[currentForm] = addresses.length > 0;
      const addressFieldConfig: FormlyFieldConfig | undefined = this.localConfig
        ?.find(x => x.key === currentForm)
        ?.fieldGroup?.find(x => x.key === 'addressLineLookUp' && x.type === FormFieldType.Select);

      if (addressFieldConfig?.props && this.addressSearchSuccessful[currentForm]) {
        addressFieldConfig.props.options = addresses.map(a => ({ value: a.id, label: a.text }));
        this.addresses[currentForm] = addresses;
      }
      this.localForm?.get([currentForm, 'postcode'])?.updateValueAndValidity();
    });
  }

  private selectFormByType(formType: DocumentPreferenceInfoType) {
    switch (formType) {
      case DocumentPreferenceInfoType.BrokerDetails:
        return 'broker';
      case DocumentPreferenceInfoType.ComplaintsInformation:
        return 'complaints';
      case DocumentPreferenceInfoType.EvidenceOfResearch:
        return 'evidenceOfResearch';
      default:
        return '';
    }
  }

  public markAllAsTouched() {
    this.localForm?.markAllAsTouched();
  }

  public saveDocPreferences(showToast = true): Promise<void> {
    if (!this.localModel) {
      return Promise.reject();
    }

    if (
      !this.isManualAddress.complaints &&
      !this.addressSearchSuccessful.complaints &&
      !this.localModel?.distributionGroupComplaints // If distribution group controls complaints info, there will be nothing to validate
    ) {
      this.triggerAddressValidation(DocumentPreferenceInfoType.ComplaintsInformation);
    }

    if (!this.isManualAddress.broker && !this.addressSearchSuccessful.broker) {
      this.triggerAddressValidation(DocumentPreferenceInfoType.BrokerDetails);
    }

    return this.localForm?.valid
      ? firstValueFrom(
        this.userDetailsService.saveEsisDocPreferences(this.localModel).pipe(
          tap(() => {
            if (showToast) {
              this.toastService.success('Successfully saved document preferences');
            }
            this.savePreferences.emit(this.localForm?.value);
          }),
        ),
      )
      : Promise.reject();
  }

  private triggerAddressValidation(formType: DocumentPreferenceInfoType): void {
    const currentForm = this.selectFormByType(formType);
    if (currentForm === 'evidenceOfResearch' || currentForm === '') {
      return;
    }
    this.addressSearchSuccessful[currentForm] = true;
    this.searchAddress(formType);
    this.changeDetectorRef.detectChanges();
    this.localForm?.markAllAsTouched();
  }
}
